import React, { useEffect, useState } from "react";
import { Loading } from "../../../common/components/Loading";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import {
  DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  DevInstructionsSurveySlug,
  getDevInstructionsCompletionStatus,
  PrivacyNoticeChecklistItems,
  usePrivacyNoticeChecklistItems,
} from "../../pages/stay-compliant/PrivacyNoticeChecklistItems";
import { Breadcrumbs, Modal } from "@truevault/aquilo";
import { Routes } from "../../root/routes";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { ProgressFooter, ProgressFooterActions } from "../../pages/get-compliant/ProgressFooter";
import { useHistory } from "react-router";

export function StayCompliantPublishNoticesPage() {
  const history = useHistory();
  const [org] = usePrimaryOrganization();

  // State
  const [readyToRender, setReadyToRender] = useState(false);

  const { checklistProps, ready } = usePrivacyNoticeChecklistItems(org.devInstructionsId);

  const { answers: devInstructionsSurveyAnswers, setAnswer: setDevInstructionsSurveyAnswer } =
    useSurvey(org?.id, DEVELOPER_INSTRUCTIONS_SURVEY_NAME);

  const [allVendors, ,] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );

  const { allDevInstructionsNoticesFinished } = getDevInstructionsCompletionStatus(
    devInstructionsSurveyAnswers,
    checklistProps?.notices,
    allVendors,
  );

  // Keep track of whether to show the footer in a separate state variable that is only initialized on page load, so we know to persist the footer even after the user has completed this step
  const [showFooter, setShowFooter] = useState<boolean | null>(null);
  useEffect(() => {
    if (
      showFooter === null &&
      devInstructionsSurveyAnswers &&
      checklistProps?.notices &&
      allVendors &&
      allVendors.length > 0
    ) {
      const { allDevInstructionsNoticesFinished } = getDevInstructionsCompletionStatus(
        devInstructionsSurveyAnswers,
        checklistProps?.notices,
        allVendors,
      );
      setShowFooter(!allDevInstructionsNoticesFinished);
    }
  }, [devInstructionsSurveyAnswers, checklistProps, showFooter, setShowFooter]);

  const [showOnboardingModal, setShowOnboardingModal] = useState(true);

  useEffect(() => {
    if (ready) {
      setReadyToRender(true);
    }
  }, [ready]);

  if (!readyToRender) {
    return (
      <StandardPageWrapper>
        <Loading />
      </StandardPageWrapper>
    );
  }

  return (
    <StandardPageWrapper
      footer={
        showFooter && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Developer Instructions"
                nextDisabled={!allDevInstructionsNoticesFinished}
                nextOnClick={() => {
                  // Even though this is also set in a useEffect in PrivacyNoticeChecklistItems, let's also set it here to ensure the step is marked as done when the user navigates back to the dashboard
                  setDevInstructionsSurveyAnswer(DevInstructionsSurveySlug, "true");
                  history.push(Routes.privacyCenter.consentBanner);
                }}
              />
            }
          />
        )
      }
    >
      <div className="tw-boundary">
        <Breadcrumbs title="Developer Instructions" sourceRoute={Routes.privacyCenter.root} />
      </div>

      {showOnboardingModal && !allDevInstructionsNoticesFinished && (
        <OnboardingModal closeModal={() => setShowOnboardingModal(false)} />
      )}

      {checklistProps?.notices && (
        <PrivacyNoticeChecklistItems {...checklistProps} stayCompliant={true} />
      )}
    </StandardPageWrapper>
  );
}

function OnboardingModal({ closeModal }: { closeModal: () => void }) {
  return (
    <Modal
      title="Follow the instructions below to get your notices live on your site or share the instructions with your developer"
      body="Scan your site when done to make sure all the links work."
      alignText="center"
      firstButton="Continue"
      firstButtonColor="primary"
      alignButton="center"
      widthButton="full"
      onClose={closeModal}
      showOverlay
      closeButton
    >
      <img
        src="/assets/images/stay_compliant/privacy_center/first-time-developer-instructions-illustration.svg"
        className="mx-auto w-[358px] h-[264px]"
      />
    </Modal>
  );
}
